<template>
  <div class="pdf-doc">
    <div class="pdf-header">
      <img
        src="@/assets/images/logo/realtor-logo.svg"
        class="pdf-logo"
      />
      <div class="pdf-company">
        <div class="pdf-company-name">FindBangkokRoom Co.,Ltd</div>
        <div>29 Soi Sukhumvit 18, Sukhumvit Road</div>
        <div>Klongtoey, Klongtoey, Bangkok 10110</div>
        <div>Tel. +66(0)-97-426-5147</div>
        <div>https://therealtors.net</div>
      </div>
    </div>

    <div class="pdf-title-block">
      <div class="pdf-title-row">
        <div>
          <h4 class="pdf-title" v-html="bilingual('Property Tax Calculation', 'คำนวณภาษีและค่าธรรมเนียมโอนอสังหาริมทรัพย์')"></h4>
          <div v-if="propertyLine" class="pdf-property-line">{{ propertyLine }}</div>
        </div>
        <div class="pdf-agent">
          <div v-if="agentName">Prepared By (Agent): <strong>{{ agentName }}</strong></div>
          <div>{{ formatDMY() }}</div>
        </div>
      </div>
    </div>

    <table class="pdf-layout-table">
      <tbody>
        <tr>
          <td>
            <h5 class="pdf-section-title" v-html="bilingual('Property Information', 'ข้อมูลทรัพย์สิน')"></h5>
            <table class="pdf-kv-table">
              <tbody>
                <tr v-if="projectName">
                  <td v-html="bilingual('Project', 'โครงการ')"></td>
                  <td>{{ projectName }}</td>
                </tr>
                <tr v-if="unitNo">
                  <td v-html="bilingual('Unit No.', 'เลขที่ห้อง/ยูนิต')"></td>
                  <td>{{ unitNo }}</td>
                </tr>
                <tr v-if="tower">
                  <td v-html="bilingual('Tower', 'อาคาร/ตึก')"></td>
                  <td>{{ tower }}</td>
                </tr>
                <tr v-if="floor">
                  <td v-html="bilingual('Floor', 'ชั้น')"></td>
                  <td>{{ floor }}</td>
                </tr>
                <tr v-if="unitArea">
                  <td v-html="bilingual('Unit Area', 'พื้นที่ห้อง/ยูนิต')"></td>
                  <td>{{ formatNumber(unitArea) }} sqm</td>
                </tr>
                <tr v-if="appraisedRatePerSqm">
                  <td v-html="bilingual('Appraised Rate / sqm', 'ราคาประเมิน/ตร.ม.')"></td>
                  <td>฿ {{ formatNumber(appraisedRatePerSqm) }}</td>
                </tr>
                <tr v-if="ownerName">
                  <td v-html="bilingual('Owner', 'เจ้าของ')"></td>
                  <td>{{ ownerName }}</td>
                </tr>
              </tbody>
            </table>
          </td>
          <td>
            <h5 class="pdf-section-title" v-html="bilingual('Sale & Tax Calculation', 'การคำนวณราคาขายและภาษี')"></h5>
            <table class="pdf-kv-table">
              <tbody>
                <tr>
                  <td v-html="bilingual('Selling Price', 'ราคาขาย')"></td>
                  <td>฿ {{ formatNumber(salePrice) }}</td>
                </tr>
                <tr>
                  <td v-html="bilingual('Appraised Value', 'ราคาประเมิน')"></td>
                  <td>฿ {{ formatNumber(appraisedValue) }}</td>
                </tr>
                <tr>
                  <td v-html="bilingual('Occupied Date', 'วันที่ได้มา/เข้าครอบครอง')"></td>
                  <td>{{ formatGregorian(dateAcquired) }}</td>
                </tr>
                <tr>
                  <td v-html="bilingual('Transfer Date', 'วันที่โอนกรรมสิทธิ์')"></td>
                  <td>{{ formatGregorian(transferDate) }}</td>
                </tr>
                <tr>
                  <td v-html="bilingual('Years of Possession', 'ระยะเวลาการถือครอง')"></td>
                  <td>{{ result.holdingYears }} year(s)</td>
                </tr>
                <tr v-if="sellerType === 'individual'">
                  <td v-html="bilingual('Calendar Year', 'ปีปฏิทิน')"></td>
                  <td>{{ result.calendarYear }} year(s)</td>
                </tr>
                <tr>
                  <td v-html="bilingual('Seller Type', 'ประเภทผู้ขาย')"></td>
                  <td v-html="sellerType === 'juristic'
                    ? bilingual('Juristic (Company)', 'นิติบุคคล (บริษัท)')
                    : bilingual('Individual', 'บุคคลธรรมดา')"
                  ></td>
                </tr>
                <tr v-if="sellerType === 'individual'">
                  <td
                    colspan="2"
                    class="pdf-note"
                    v-html="isPrimaryResidenceExempt
                      ? bilingual('Registered its name in a House Registration Book for 1 year or more', 'มีชื่อในทะเบียนบ้านตั้งแต่ 1 ปีขึ้นไป')
                      : bilingual('Never registered or registered its name in a House Registration Book for less than 1 year', 'ไม่เคยมีชื่อ หรือมีชื่อในทะเบียนบ้านน้อยกว่า 1 ปี')"
                  ></td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pdf-section-spacing pdf-highlight-box">
      <h5
        class="pdf-highlight-title"
        v-html="showCommissionInPdf
          ? bilingual('Tax, Fee & Commission Breakdown', 'รายละเอียดภาษี ค่าธรรมเนียม และค่านายหน้า')
          : bilingual('Tax & Fee Breakdown', 'รายละเอียดภาษีและค่าธรรมเนียม')"
      ></h5>
      <table class="pdf-split-table">
        <thead>
          <tr>
            <th v-html="bilingual('Item', 'รายการ')"></th>
            <th v-html="bilingual('Amount', 'จำนวนเงิน')"></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td v-html="bilingual('Transfer Fee (2%)', 'ค่าธรรมเนียมการโอน (2%)')"></td>
            <td>฿ {{ formatNumber(result.transferFee) }}</td>
          </tr>
          <tr v-if="result.sbtOrStampDuty.type === 'sbt'">
            <td v-html="bilingual('Business Tax (3.3%)', 'ภาษีธุรกิจเฉพาะ (3.3%)')"></td>
            <td>฿ {{ formatNumber(result.sbtOrStampDuty.amount) }}</td>
          </tr>
          <tr v-else>
            <td v-html="bilingual('Stamp Duty (0.5%)', 'อากรแสตมป์ (0.5%)')"></td>
            <td>฿ {{ formatNumber(result.sbtOrStampDuty.amount) }}</td>
          </tr>
          <tr>
            <td v-html="bilingual('Withholding Tax', 'ภาษีหัก ณ ที่จ่าย')"></td>
            <td>฿ {{ formatNumber(result.withholdingTax) }}</td>
          </tr>
          <tr :class="['pdf-total-row', { 'pdf-net-row': !showCommissionInPdf }]">
            <td v-html="bilingual('Tax & Fee Total', 'รวมภาษีและค่าธรรมเนียม')"></td>
            <td>฿ {{ formatNumber(result.total) }}</td>
          </tr>
          <template v-if="showCommissionInPdf">
            <tr>
              <td v-html="commissionLabel()"></td>
              <td>฿ {{ formatNumber(commissionResult.commission) }}</td>
            </tr>
            <tr v-if="commissionIncludeVat">
              <td v-html="bilingual('VAT (7%)', 'ภาษีมูลค่าเพิ่ม (7%)')"></td>
              <td>฿ {{ formatNumber(commissionResult.vat) }}</td>
            </tr>
            <tr class="pdf-total-row">
              <td v-html="bilingual('Commission Total', 'รวมค่านายหน้า')"></td>
              <td>฿ {{ formatNumber(commissionResult.total) }}</td>
            </tr>
            <tr class="pdf-total-row pdf-net-row">
              <td v-html="bilingual('Grand Total', 'ยอดรวมทั้งสิ้น')"></td>
              <td>฿ {{ formatNumber(result.total + commissionResult.total) }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <p
      class="pdf-disclaimer"
      v-html="bilingual(
        'For preparation purposes only — this is an estimate to help both parties prepare funds ahead of transfer day, not an official Land Office assessment. Please confirm final figures with your conveyancer or the Land Office on the day of transfer.',
        'จัดทำขึ้นเพื่อใช้ในการเตรียมการเท่านั้น เป็นการประมาณการเพื่อช่วยเตรียมเงินก่อนวันโอน มิใช่การประเมินอย่างเป็นทางการจากกรมที่ดิน กรุณายืนยันตัวเลขสุดท้ายกับทนายความหรือเจ้าหน้าที่กรมที่ดินในวันโอนกรรมสิทธิ์'
      )"
    ></p>

    <div v-if="titleDeedImageSrc" class="pdf-title-deed">
      <h4 v-html="bilingual('Title Deed', 'โฉนดที่ดิน')"></h4>
      <img :src="titleDeedImageSrc" />
    </div>
  </div>
</template>

<script>
import {
  formatNumber, formatGregorian, formatDMY, ordinalSuffix,
} from '../format'

export default {
  props: {
    propertyId: { type: [String, Number], required: true },
    propertyCode: { type: String, default: null },
    projectName: { type: String, default: null },
    unitNo: { type: String, default: null },
    tower: { type: String, default: null },
    floor: { type: String, default: null },
    detailProjectName: { type: String, default: null },
    detailUnitNo: { type: String, default: null },
    detailTower: { type: String, default: null },
    detailFloor: { type: String, default: null },
    unitArea: { type: Number, default: null },
    appraisedRatePerSqm: { type: Number, default: null },
    ownerName: { type: String, default: null },
    agentName: { type: String, default: null },
    salePrice: { type: Number, default: null },
    appraisedValue: { type: Number, default: 0 },
    dateAcquired: { type: String, default: null },
    transferDate: { type: String, default: null },
    result: { type: Object, required: true },
    sellerType: { type: String, required: true },
    isPrimaryResidenceExempt: { type: Boolean, default: false },
    showCommissionInPdf: { type: Boolean, default: true },
    commissionType: { type: String, required: true },
    commissionRate: { type: Number, default: null },
    commissionResult: { type: Object, required: true },
    commissionIncludeVat: { type: Boolean, default: true },
    titleDeedImageSrc: { type: String, default: null },
  },
  computed: {
    propertyLine() {
      const parts = []
      parts.push(this.propertyCode || `#${this.propertyId}`)
      if (this.detailProjectName) parts.push(this.detailProjectName)
      if (this.detailUnitNo) parts.push(`Unit No. ${this.detailUnitNo}`)
      if (this.detailFloor) parts.push(`${this.detailFloor}${ordinalSuffix(this.detailFloor)} Floor`)
      if (this.detailTower) parts.push(`Tower ${this.detailTower}`)
      return parts.join(', ')
    },
  },
  methods: {
    formatNumber,
    formatGregorian,
    formatDMY,
    bilingual(en, th) {
      return `${en}<span class="pdf-th">${th}</span>`
    },
    commissionLabel() {
      const suffix = this.commissionType === 'manual' ? '' : ` (${this.formatNumber(this.commissionRate) || 0}%)`
      return this.bilingual(`Commission${suffix}`, `ค่านายหน้า${suffix}`)
    },
  },
}
</script>
